import { initializeApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { getFirestore } from "firebase/firestore";
import { getAuth } from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

//Here we initiallize our firebase
const app = initializeApp(firebaseConfig);

// App Check attests that Firestore traffic comes from this app rather than from
// anyone who scraped the project ID out of the shipped bundle. It is a gate on
// *who is calling*, not on *what they may read* — the per-thread authorisation
// rules still depend on Firebase Auth landing (see docs/firestore.rules).
//
// Gated on the site key so a missing value degrades to today's behaviour
// instead of throwing at module load and taking the whole panel down. Once the
// key is set in every environment, turn on enforcement in the Firebase console;
// enforcing before then would lock the panel out of Firestore.
const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;

if (appCheckSiteKey) {
  // Local dev has no reCAPTCHA origin. Set REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN
  // to a token registered under App Check -> Manage debug tokens, or to `true` to
  // have the SDK print a fresh one to the console for you to register.
  if (process.env.NODE_ENV !== "production") {
    const debugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
    if (debugToken) {
      // eslint-disable-next-line no-restricted-globals
      self.FIREBASE_APPCHECK_DEBUG_TOKEN =
        debugToken === "true" ? true : debugToken;
    }
  }

  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    // A bad key or a blocked reCAPTCHA load must not break the panel. With
    // enforcement off this is cosmetic; with enforcement on, Firestore calls
    // fail on their own and surface there.
    console.error("App Check initialisation failed:", error);
  }
}

const db = getFirestore(app);

// Firestore access is authorised per-thread by docs/firestore.rules, and every
// rule in there reads request.auth. Nothing populates request.auth unless this
// client signs in to Firebase, so the auth instance is exported for
// services/firebaseChatAuth.js to sign in against with a server-minted custom
// token. Panel staff authenticate against Laravel Sanctum, not Firebase — this
// is a second, chat-scoped identity derived from the Sanctum session, never a
// separate credential the user types.
const auth = getAuth(app);

const messaging = getMessaging(app);

const requestForToken = async () => {
  try {
    // Request permission to show notifications
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return null; // Return null if permission is not granted
    }
    // Get the FCM token
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });

    if (token) {
      return token;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching FCM token:", error);
    return null;
  }
};

const onMessageListener = () => {
  return onMessage(messaging, (payload) => {
    return payload;
  });
};

export { db, auth, requestForToken, onMessageListener, messaging };
